import React, { useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";

const RETENTION_OPTIONS = [
  { hours: 24, label: "1 day" },
  { hours: 72, label: "3 days" },
  { hours: 168, label: "7 days" },
  { hours: 336, label: "14 days" },
  { hours: 720, label: "1 month (30 days)" },
  { hours: 2160, label: "3 months (90 days)" },
  { hours: 4320, label: "6 months (180 days)" },
  { hours: 8760, label: "1 year (365 days)" },
];

type Camera = {
  cameraId: string;
  archiveHours?: number;
  updatedAt?: string;
  [key: string]: unknown;
};

type Props = {
  camera: Camera;
  onUpdateCamera: (camera: any) => void;
};

function formatRetention(hours: number) {
  if (hours <= 0) return "No archive retention";
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"}`;
  if (hours % 8760 === 0) {
    const years = hours / 8760;
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  if (hours % 720 === 0) {
    const months = hours / 720;
    return `${months} month${months === 1 ? "" : "s"}`;
  }
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

async function readResponse(response: Response) {
  const text = await response.text();
  try {
    return { text, json: text ? JSON.parse(text) : null };
  } catch {
    return { text, json: null };
  }
}

async function getIdTokenOrThrow() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error("Your authentication session expired. Sign in again.");
  }
  return token;
}

export default function ArchiveRetentionTab({ camera, onUpdateCamera }: Props) {
  const storedHours = Number(camera.archiveHours);
  const currentHours = Number.isFinite(storedHours) && storedHours >= 0
    ? storedHours
    : 24;
  const [selectedHours, setSelectedHours] = useState(currentHours);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    setSelectedHours(currentHours);
    setMessage("");
    setIsError(false);
  }, [camera.cameraId, currentHours]);

  const selectedOption = useMemo(
    () => RETENTION_OPTIONS.find((option) => option.hours === selectedHours),
    [selectedHours],
  );
  const currentIsPreset = RETENTION_OPTIONS.some(
    (option) => option.hours === currentHours,
  );

  async function saveRetention() {
    if (saving || selectedHours === currentHours) return;

    if (
      selectedHours < currentHours &&
      !window.confirm(
        `Reduce archive retention from ${formatRetention(currentHours)} to ${formatRetention(selectedHours)}? Recordings older than the new duration may become unavailable.`,
      )
    ) {
      return;
    }

    setSaving(true);
    setIsError(false);
    setMessage("Updating archive retention...");

    try {
      const idToken = await getIdTokenOrThrow();
      const response = await fetch(
        `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ archiveHours: selectedHours }),
        },
      );
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          `${response.status}: ${
            json?.message || json?.error || text || response.statusText ||
            "Unable to update archive retention."
          }`,
        );
      }

      onUpdateCamera({
        ...camera,
        ...(json?.camera || {}),
        archiveHours: selectedHours,
        updatedAt: json?.camera?.updatedAt || new Date().toISOString(),
      });
      setMessage(`Archive retention updated to ${formatRetention(selectedHours)}.`);
    } catch (error) {
      setIsError(true);
      setMessage(
        `Unable to update archive retention: ${
          error instanceof Error ? error.message : "Unexpected error"
        }`,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      style={{
        display: "grid",
        gap: 20,
        padding: 22,
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        background: "#ffffff",
      }}
    >
      <div>
        <div
          style={{
            color: "#2563eb",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".08em",
            textTransform: "uppercase",
          }}
        >
          Cloud archive
        </div>
        <h3 style={{ margin: "5px 0 7px", color: "#0f172a" }}>
          Archive Retention
        </h3>
        <p style={{ margin: 0, color: "#64748b", lineHeight: 1.55 }}>
          Choose how long recorded video will be kept in the cloud. The current
          selection comes from the archive duration chosen when this camera was added.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(240px, 1.15fr) minmax(240px, .85fr)",
          gap: 18,
          alignItems: "end",
        }}
      >
        <label
          htmlFor={`archive-retention-${camera.cameraId}`}
          style={{ display: "grid", gap: 8, color: "#334155", fontWeight: 800 }}
        >
          <span>Retention period *</span>
          <select
            id={`archive-retention-${camera.cameraId}`}
            aria-label="Retention period"
            value={selectedHours}
            onChange={(event) => {
              setSelectedHours(Number(event.target.value));
              setMessage("");
              setIsError(false);
            }}
            disabled={saving}
            style={{
              width: "100%",
              minHeight: 48,
              padding: "10px 13px",
              border: "1px solid #94a3b8",
              borderRadius: 11,
              color: "#0f172a",
              background: "#ffffff",
              font: "inherit",
              fontWeight: 700,
            }}
          >
            {!currentIsPreset && (
              <option value={currentHours}>
                Current setting — {formatRetention(currentHours)}
              </option>
            )}
            {RETENTION_OPTIONS.map((option) => (
              <option key={option.hours} value={option.hours}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div
          style={{
            minHeight: 48,
            display: "flex",
            alignItems: "center",
            padding: "10px 14px",
            border: "1px solid #bfdbfe",
            borderRadius: 11,
            color: "#334155",
            background: "#eff6ff",
            lineHeight: 1.45,
          }}
        >
          Recordings will be automatically deleted after {selectedOption?.label || formatRetention(selectedHours)}.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        <Summary label="Current retention" value={formatRetention(currentHours)} />
        <Summary
          label="Selected retention"
          value={selectedOption?.label || formatRetention(selectedHours)}
        />
        <Summary
          label="Change"
          value={
            selectedHours === currentHours
              ? "No change"
              : selectedHours > currentHours
                ? "Increase retention"
                : "Reduce retention"
          }
        />
      </div>

      {message && (
        <div
          role={isError ? "alert" : "status"}
          style={{
            padding: "11px 13px",
            border: `1px solid ${isError ? "#fecaca" : "#bbf7d0"}`,
            borderRadius: 10,
            color: isError ? "#b91c1c" : "#166534",
            background: isError ? "#fef2f2" : "#f0fdf4",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {message}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => void saveRetention()}
          disabled={saving || selectedHours === currentHours}
          style={{
            minHeight: 42,
            padding: "10px 17px",
            border: 0,
            borderRadius: 11,
            color: "#ffffff",
            background: "#111827",
            fontWeight: 800,
            cursor:
              saving || selectedHours === currentHours ? "not-allowed" : "pointer",
            opacity: saving || selectedHours === currentHours ? 0.55 : 1,
          }}
        >
          {saving ? "Saving..." : "Save Archive Retention"}
        </button>
      </div>
    </section>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: 14,
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        background: "#f8fafc",
      }}
    >
      <div
        style={{
          marginBottom: 5,
          color: "#64748b",
          fontSize: 10,
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <strong style={{ color: "#0f172a" }}>{value}</strong>
    </div>
  );
}
